import {
  BIP39_WORD_INDICES_COUNT,
  BLEND_SEED_LENGTH,
  RAW_SENTENCE_BYTES_LENGTH,
  VERBOSE_SLOT_COUNT,
  type Bip39WordIndices,
  type CompactScore,
  type EncodeResult,
  type ScoreInput,
  type Scorer,
  type VerboseScore,
} from "./types";

// 4 (n_sentences) + 1 (flags) + 32 (seed) + 60 (payload) + 10 (5 × u16 BIP39).
const SCORE_INPUT_BYTES = 107;
// 4 (n_results) + 1 (gates_pass) + 8 (canonicalBlend u64 LE).
const SCORE_COMPACT_OUTPUT_BYTES = 13;
// Compact + 512 (256 × u16 scorer outputs).
const SCORE_VERBOSE_OUTPUT_BYTES = SCORE_COMPACT_OUTPUT_BYTES + VERBOSE_SLOT_COUNT * 2;

// Generous upper bound for decoded sentence text. Matches the C# host.
const DECODE_MAX_OUTPUT_BYTES = 1024;

interface WasmExports {
  readonly memory: WebAssembly.Memory;
  readonly _initialize?: () => void;
  readonly sb_alloc: (len: number) => number;
  readonly sb_free: (ptr: number) => void;
  readonly encode_sentence_wasm: (textPtr: number, textLen: number, payloadPtr: number) => number;
  readonly decode_sentence_wasm: (payloadPtr: number, textPtr: number, maxLen: number) => number;
  readonly score_sentences_wasm: (
    inputPtr: number,
    inputLen: number,
    blobPtr: number,
    blobLen: number,
    outputPtr: number,
  ) => number;
}

function assertExports(exports: WebAssembly.Exports): WasmExports {
  const required = [
    "memory",
    "sb_alloc",
    "sb_free",
    "encode_sentence_wasm",
    "decode_sentence_wasm",
    "score_sentences_wasm",
  ] as const;
  for (const name of required) {
    if (!(name in exports)) {
      throw new Error(`scoring WASM is missing required export '${name}'`);
    }
  }
  return exports as unknown as WasmExports;
}

/**
 * In-process protocol scorer. Wraps the canonical `cogcoin_scoring.wasm`
 * from `@cogcoin/genesis` and exposes the same surface the C# host does
 * (see `../sentencing/.../WasmSentenceScorer.cs`).
 *
 * The CGSM model blob is loaded into linear memory once at construction
 * time and reused for every call. The instance is not thread-safe — but
 * JavaScript is single-threaded, so unless an `await` is introduced
 * inside a scoring call, that is moot.
 */
export class WasmScorer implements Scorer {
  static async create(wasmBytes: BufferSource, blobBytes: Uint8Array): Promise<WasmScorer> {
    const { instance } = await WebAssembly.instantiate(wasmBytes, {});
    return new WasmScorer(instance, blobBytes);
  }

  static async fromStreams(
    wasmResponse: Response | Promise<Response>,
    blobResponse: Response | Promise<Response>,
  ): Promise<WasmScorer> {
    const [{ instance }, blobArrayBuffer] = await Promise.all([
      WebAssembly.instantiateStreaming(wasmResponse, {}),
      Promise.resolve(blobResponse).then((r) => r.arrayBuffer()),
    ]);
    return new WasmScorer(instance, new Uint8Array(blobArrayBuffer));
  }

  private readonly exports: WasmExports;
  private readonly blobPtr: number;
  private readonly blobLen: number;
  private readonly textEncoder = new TextEncoder();
  private readonly textDecoder = new TextDecoder();

  private constructor(instance: WebAssembly.Instance, blobBytes: Uint8Array) {
    this.exports = assertExports(instance.exports);
    this.exports._initialize?.();
    this.blobLen = blobBytes.length;
    this.blobPtr = this.exports.sb_alloc(blobBytes.length);
    this.bytesAt(this.blobPtr, blobBytes.length).set(blobBytes);
  }

  encodeSentence(text: string): EncodeResult {
    const utf8 = this.textEncoder.encode(text);
    // sb_alloc(0) is undefined territory; allocate at least one byte.
    const textPtr = this.exports.sb_alloc(utf8.length === 0 ? 1 : utf8.length);
    const payloadPtr = this.exports.sb_alloc(RAW_SENTENCE_BYTES_LENGTH);
    try {
      if (utf8.length > 0) {
        this.bytesAt(textPtr, utf8.length).set(utf8);
      }
      const ret = this.exports.encode_sentence_wasm(textPtr, utf8.length, payloadPtr);
      if (ret >= 0) {
        const rawSentenceBytes = new Uint8Array(RAW_SENTENCE_BYTES_LENGTH);
        rawSentenceBytes.set(this.bytesAt(payloadPtr, RAW_SENTENCE_BYTES_LENGTH));
        return { status: "success", rawSentenceBytes, tokenCount: ret };
      }
      switch (ret) {
        case -1:
          return { status: "invalid-input" };
        case -2:
          return { status: "word-not-in-vocab" };
        case -3:
          return { status: "exceeds-token-limit" };
        default:
          throw new Error(`encode_sentence_wasm returned unexpected error code ${ret}`);
      }
    } finally {
      this.exports.sb_free(payloadPtr);
      this.exports.sb_free(textPtr);
    }
  }

  decodeSentence(rawSentenceBytes: Uint8Array): string {
    if (rawSentenceBytes.length !== RAW_SENTENCE_BYTES_LENGTH) {
      throw new Error(
        `rawSentenceBytes must be ${RAW_SENTENCE_BYTES_LENGTH} bytes; was ${rawSentenceBytes.length}`,
      );
    }
    const payloadPtr = this.exports.sb_alloc(RAW_SENTENCE_BYTES_LENGTH);
    const textPtr = this.exports.sb_alloc(DECODE_MAX_OUTPUT_BYTES);
    try {
      this.bytesAt(payloadPtr, RAW_SENTENCE_BYTES_LENGTH).set(rawSentenceBytes);
      const len = this.exports.decode_sentence_wasm(payloadPtr, textPtr, DECODE_MAX_OUTPUT_BYTES);
      if (len < 0) {
        const reason =
          len === -1
            ? "invalid arguments"
            : len === -2
              ? "token ID out of range"
              : len === -3
                ? "output buffer too small"
                : `unknown error code ${len}`;
        throw new Error(`decode_sentence_wasm failed: ${reason}`);
      }
      return this.textDecoder.decode(this.bytesAt(textPtr, len));
    } finally {
      this.exports.sb_free(textPtr);
      this.exports.sb_free(payloadPtr);
    }
  }

  score(input: ScoreInput): CompactScore {
    return this.scoreInternal(input, false);
  }

  scoreVerbose(input: ScoreInput): VerboseScore {
    return this.scoreInternal(input, true) as VerboseScore;
  }

  private scoreInternal(input: ScoreInput, verbose: boolean): CompactScore | VerboseScore {
    this.validateScoreInput(input);

    const outputBytes = verbose ? SCORE_VERBOSE_OUTPUT_BYTES : SCORE_COMPACT_OUTPUT_BYTES;
    const inputPtr = this.exports.sb_alloc(SCORE_INPUT_BYTES);
    const outputPtr = this.exports.sb_alloc(outputBytes);
    try {
      this.writeScoreInput(inputPtr, input, verbose);
      const n = this.exports.score_sentences_wasm(
        inputPtr,
        SCORE_INPUT_BYTES,
        this.blobPtr,
        this.blobLen,
        outputPtr,
      );
      if (n !== 1) {
        throw new Error(`score_sentences_wasm returned ${n}; expected 1`);
      }
      const view = new DataView(this.exports.memory.buffer, outputPtr, outputBytes);
      const gatesPass = view.getUint8(4) === 1;
      const canonicalBlend = view.getBigUint64(5, true);
      if (!verbose) {
        return { gatesPass, canonicalBlend };
      }
      const scorerOutputs = new Uint16Array(VERBOSE_SLOT_COUNT);
      for (let i = 0; i < VERBOSE_SLOT_COUNT; i++) {
        scorerOutputs[i] = view.getUint16(13 + i * 2, true);
      }
      return { gatesPass, canonicalBlend, scorerOutputs };
    } finally {
      this.exports.sb_free(outputPtr);
      this.exports.sb_free(inputPtr);
    }
  }

  private writeScoreInput(ptr: number, input: ScoreInput, verbose: boolean): void {
    const view = new DataView(this.exports.memory.buffer, ptr, SCORE_INPUT_BYTES);
    view.setUint32(0, 1, true); // n_sentences = 1
    view.setUint8(4, verbose ? 1 : 0);
    this.bytesAt(ptr + 5, BLEND_SEED_LENGTH).set(input.blendSeed);
    this.bytesAt(ptr + 37, RAW_SENTENCE_BYTES_LENGTH).set(input.rawSentenceBytes);
    for (let i = 0; i < BIP39_WORD_INDICES_COUNT; i++) {
      view.setUint16(97 + i * 2, input.bip39WordIndices[i]!, true);
    }
  }

  private validateScoreInput(input: ScoreInput): void {
    if (input.rawSentenceBytes.length !== RAW_SENTENCE_BYTES_LENGTH) {
      throw new Error(
        `rawSentenceBytes must be ${RAW_SENTENCE_BYTES_LENGTH} bytes; was ${input.rawSentenceBytes.length}`,
      );
    }
    if (input.blendSeed.length !== BLEND_SEED_LENGTH) {
      throw new Error(`blendSeed must be ${BLEND_SEED_LENGTH} bytes; was ${input.blendSeed.length}`);
    }
    // Tuple length is enforced by the `Bip39WordIndices` type. Callers that
    // start from an untyped array should pipe through `asBip39WordIndices`.
    for (const idx of input.bip39WordIndices) {
      if (!Number.isInteger(idx) || idx < 0 || idx > 0xffff) {
        throw new Error(`bip39WordIndices must be u16; got ${idx}`);
      }
    }
  }

  // The Memory.buffer is detached whenever the memory grows; rebuild views
  // on every access. Allocations can grow memory, so don't cache views
  // across `sb_alloc` calls.
  private bytesAt(ptr: number, len: number): Uint8Array {
    return new Uint8Array(this.exports.memory.buffer, ptr, len);
  }
}

/// Convenience for callers that have a `Bip39WordIndices`-shaped array as
/// `number[]` (e.g. from JSON). Validates length, returns the typed tuple.
export function asBip39WordIndices(indices: readonly number[]): Bip39WordIndices {
  if (indices.length !== BIP39_WORD_INDICES_COUNT) {
    throw new Error(
      `bip39WordIndices must have ${BIP39_WORD_INDICES_COUNT} entries; was ${indices.length}`,
    );
  }
  return [
    indices[0]!,
    indices[1]!,
    indices[2]!,
    indices[3]!,
    indices[4]!,
  ];
}
